import { Box, SxProps, Theme } from '@mui/material';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useDeathOrderAwareClient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import { PrimaryClientDeathEvent } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { COLORS } from '@/styles/tokens/colors';

interface PrimaryClientDeathSectionLabelProps {
  deathEvent?: PrimaryClientDeathEvent;
  /**
   * When provided, this string is rendered as the badge label and the
   * `deathEvent`-driven label is bypassed. Used by callers (e.g. the BDG
   * form) that want the same dashed-divider + centered badge UI without
   * the primary-client death-order semantics — those callers don't render
   * inside the DispositiveProvisionsContext, so the death-order hook must
   * not run.
   */
  label?: string;
  containerSx?: SxProps<Theme>;
}

export function PrimaryClientDeathSectionLabel({
  deathEvent,
  label,
  containerSx,
}: PrimaryClientDeathSectionLabelProps) {
  // The death-order hook depends on DispositiveProvisionsContext and throws
  // when there's no dying client, so callers that pass an explicit `label`
  // must skip the death-event branch entirely.
  if (label !== undefined) {
    return (
      <PrimaryClientDeathSectionLabelContent
        label={label}
        containerSx={containerSx}
      />
    );
  }

  return <DeathEventLabel deathEvent={deathEvent} containerSx={containerSx} />;
}

interface DeathEventLabelProps {
  deathEvent?: PrimaryClientDeathEvent;
  containerSx?: SxProps<Theme>;
}

function DeathEventLabel({ deathEvent, containerSx }: DeathEventLabelProps) {
  const dyingPrimaryClient = useDeathOrderAwareClient(
    deathEvent ? { deathEvent } : undefined
  );

  let label = '';
  if (dyingPrimaryClient?.displayGivenName) {
    label = `After ${dyingPrimaryClient.displayGivenName}'s death`;
  } else if (deathEvent) {
    label = `After ${deathEventDisplayNames[deathEvent]} death`;
  }

  return (
    <PrimaryClientDeathSectionLabelContent
      label={label}
      containerSx={containerSx}
    />
  );
}

interface PrimaryClientDeathSectionLabelContentProps {
  label: string;
  containerSx?: SxProps<Theme>;
}

function PrimaryClientDeathSectionLabelContent({
  label,
  containerSx,
}: PrimaryClientDeathSectionLabelContentProps) {
  return (
    <Box position="relative" width="100%" sx={containerSx}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          width: '100%',
          transform: 'translateY(-50%)',
          zIndex: 0,
          // custom dashes -- because CSS doesn't let you do this, work around it
          // by drawing a 1px tall box and creating a background SVG
          // https://codepen.io/amit_sheen/pen/xxZeyjO
          borderStyle: 'none',
          height: '1px',
          backgroundImage: `repeating-linear-gradient(90deg, ${COLORS.GRAY[300]} 0, ${COLORS.GRAY[300]} ${4}px, transparent ${4}px, transparent ${8}px)`,
          backgroundSize: `${8}px 1px`,
          backgroundPosition: '0 0, 0 0, 100% 0, 0 100%;',
          backgroundRepeat: 'repeat-x;',
        }}
      />
      <Box
        display="flex"
        justifyContent="center"
        position="relative"
        zIndex={1}
      >
        <Badge variant={BadgeVariants.SecondaryOutline} display={label} />
      </Box>
    </Box>
  );
}

const deathEventDisplayNames: Record<PrimaryClientDeathEvent, string> = {
  [PrimaryClientDeathEvent.FIRST_DEATH]: 'first',
  [PrimaryClientDeathEvent.SECOND_DEATH]: 'second',
};
